import { ReloadOutlined, StarOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, message, Skeleton, Tag, Empty } from 'antd';
import React, { useState } from 'react';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_TEMPLATE_QUERY } from '@src/utils/sensor/track';
import { NEXA_QUERY_TEMPLATE_CLICK } from '../utils';
import useGetTokenUsage from '../hooks/useGetTokenUsage';
import { useGetMyAnalyses } from '../ManageAnalyses/hooks/useGetMyAnalyses';
import { useGetTeamAnalyses } from '../ManageAnalyses/hooks/useGetTeamAnalyses';
import useGetRecommendedAnalyses from '../ManageAnalyses/hooks/useGetRecommendedAnalyses';
import NexaTitleBanner from './NexaTitleBanner';
import { StyledCard, TryPromptsTitle, EmptyStateWrapper } from './styled';
import ViewAllDrawer from './ViewAllDrawer';
import { canSeeDeepModeFeatures } from '../utils/deepModeAccess';

const RecommendedAnalysesSection = () => {
  const { isOver } = useGetTokenUsage();
  const { data = [], isFetching } = useGetRecommendedAnalyses();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const handleClick = (item: any) => {
    if (isOver) {
      message.error('Usage limit reached. Please contact support to continue using Nexa.');
      return;
    }

    const detail: any = {
      query: item.query,
      analysis_id: item.id,
      deep_mode: item.deep_mode,
    };

    window.dispatchEvent(new CustomEvent(NEXA_QUERY_TEMPLATE_CLICK, { detail }));
    apptracker(NEXA_TEMPLATE_QUERY, { value: item.query, analysis_id: item.id });
  };

  if (isFetching) {
    return (
      <StyledCard
        title={
          <span>
            <StarOutlined style={{ marginRight: 8 }} />
            Recommended Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <Skeleton active paragraph={{ rows: 3 }} />
      </StyledCard>
    );
  }

  if (data.length === 0) {
    return (
      <StyledCard
        title={
          <span>
            <StarOutlined style={{ marginRight: 8 }} />
            Recommended Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <Empty description="No recommended analyses available" />
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard
        title={
          <span>
            <StarOutlined style={{ marginRight: 8 }} />
            Recommended Analyses
          </span>
        }
        style={{ height: '100%' }}
        actions={
          data.length > 3
            ? [
                <div
                  key="view-all"
                  onClick={() => setDrawerOpen(true)}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  View all {data.length} analyses →
                </div>,
              ]
            : undefined
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.slice(0, 3).map((item: any) => (
            <Button
              key={item.id}
              onClick={() => handleClick(item)}
              block
              style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.query}
              </span>
              {canSeeDeepMode && item.deep_mode && <Tag color="blue">Deep</Tag>}
            </Button>
          ))}
        </div>
      </StyledCard>
      <ViewAllDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        category="recommended"
        data={data}
        isLoading={isFetching}
      />
    </>
  );
};

const MyAnalysesSection = () => {
  const { isOver } = useGetTokenUsage();
  const { data = [], isFetching } = useGetMyAnalyses();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const handleClick = (item: any) => {
    if (isOver) {
      message.error('Usage limit reached. Please contact support to continue using Nexa.');
      return;
    }

    const detail: any = {
      query: item.query,
      analysis_id: item.id,
      deep_mode: item.deep_mode,
    };

    window.dispatchEvent(new CustomEvent(NEXA_QUERY_TEMPLATE_CLICK, { detail }));
    apptracker(NEXA_TEMPLATE_QUERY, { value: item.query, analysis_id: item.id });
  };

  if (isFetching) {
    return (
      <StyledCard
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Private Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <Skeleton active paragraph={{ rows: 3 }} />
      </StyledCard>
    );
  }

  if (data.length === 0) {
    return (
      <StyledCard
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Private Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <EmptyStateWrapper>
          <Empty
            description={`No saved analyses yet\nSave an analysis to quickly reuse\nyour investigations`}
          />
        </EmptyStateWrapper>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            Private Analyses
          </span>
        }
        style={{ height: '100%' }}
        actions={
          data.length > 3
            ? [
                <div
                  key="view-all"
                  onClick={() => setDrawerOpen(true)}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  View all {data.length} analyses →
                </div>,
              ]
            : undefined
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.slice(0, 3).map((item: any) => (
            <Button
              key={item.id}
              onClick={() => handleClick(item)}
              block
              style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.query}
              </span>
              {canSeeDeepMode && item.deep_mode && <Tag color="blue">Deep</Tag>}
            </Button>
          ))}
        </div>
      </StyledCard>
      <ViewAllDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        category="my"
        data={data}
        isLoading={isFetching}
      />
    </>
  );
};

const TeamAnalysesSection = () => {
  const { isOver } = useGetTokenUsage();
  const { data = [], isFetching } = useGetTeamAnalyses();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const canSeeDeepMode = canSeeDeepModeFeatures();

  const handleClick = (item: any) => {
    if (isOver) {
      message.error('Usage limit reached. Please contact support to continue using Nexa.');
      return;
    }

    const detail: any = {
      query: item.query,
      analysis_id: item.id,
      deep_mode: item.deep_mode,
    };

    window.dispatchEvent(new CustomEvent(NEXA_QUERY_TEMPLATE_CLICK, { detail }));
    apptracker(NEXA_TEMPLATE_QUERY, { value: item.query, analysis_id: item.id });
  };

  if (isFetching) {
    return (
      <StyledCard
        title={
          <span>
            <TeamOutlined style={{ marginRight: 8 }} />
            Team Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <Skeleton active paragraph={{ rows: 3 }} />
      </StyledCard>
    );
  }

  if (data.length === 0) {
    return (
      <StyledCard
        title={
          <span>
            <TeamOutlined style={{ marginRight: 8 }} />
            Team Analyses
          </span>
        }
        style={{ height: '100%' }}
      >
        <EmptyStateWrapper>
          <Empty
            style={{ margin: 0 }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No team analyses yet\nSave an analysis and mark it as "Team"\nto reuse investigations across your org.`}
          />
        </EmptyStateWrapper>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard
        title={
          <span>
            <TeamOutlined style={{ marginRight: 8 }} />
            Team Analyses
          </span>
        }
        style={{ height: '100%' }}
        actions={
          data.length > 3
            ? [
                <div
                  key="view-all"
                  onClick={() => setDrawerOpen(true)}
                  style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                  View all {data.length} analyses →
                </div>,
              ]
            : undefined
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.slice(0, 3).map((item: any) => (
            <Button
              key={item.id}
              onClick={() => handleClick(item)}
              block
              style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.query}
              </span>
              {canSeeDeepMode && item.deep_mode && <Tag color="blue">Deep</Tag>}
            </Button>
          ))}
        </div>
      </StyledCard>
      <ViewAllDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        category="team"
        data={data}
        isLoading={isFetching}
      />
    </>
  );
};

const Templates = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginTop: 50 }}>
      <NexaTitleBanner />

      {/* Three sections side-by-side with responsive flow-down */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 20,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: 300, maxWidth: 500 }}>
          <RecommendedAnalysesSection />
        </div>
        <div style={{ flex: '1 1 300px', minWidth: 300, maxWidth: 500 }}>
          <MyAnalysesSection />
        </div>
        <div style={{ flex: '1 1 300px', minWidth: 300, maxWidth: 500 }}>
          <TeamAnalysesSection />
        </div>
      </div>
    </div>
  );
};

export default Templates;
